import React, { useState, useEffect } from "react";

import * as APIModels from "../../../../models";
import { AttorneyService } from "../../../../services/ClientService";
import { Alert, Card, Checkbox, Stack } from "@mui/material";
import { CardContent } from "@material-ui/core";
import { CardFooter } from "react-bootstrap";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

export default function FTP({ attorney }: { attorney: APIModels.Attorney }) {
    // from the server or undefinded
    const [ftpinfo, setftpinfo] = useState<
        APIModels.FTPInfoResponse | undefined
    >({} as APIModels.FTPInfoResponse);
    const [editingFTPInfo, setEditingFTPInfo] =
        useState<APIModels.FTPInfoRequest>();
    const [editing, setEditing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState<
        string | undefined
    >("");
    const [isCreate, setIsCreate] = useState(ftpinfo === undefined);
    const [successMessage, setSuccessMessage] = useState<string | undefined>(
        undefined
    );

    const refresh = async () => {
        console.log("Refreshing FTP Info for Attorney " + attorney.fullName);
        const response = await AttorneyService.singleFtpInfo({
            attorneyId: attorney.id ?? "",
        });
        console.log(response);
        if (response.successful && response.data) {
            setftpinfo(response.data);
        } else if (response.successful && response.data === undefined) {
            setSuccessMessage(response.errorMessage);
            setErrorModalMessage(undefined);
        } else {
            handleOnError(
                "An Error occued when fething FTP Info. " +
                    response.errorMessage ??
                    "An unknown error occured. Please contact support."
            );
        }
    };

    const getUsername = () => {
        if (editingFTPInfo) {
            return editingFTPInfo.username;
        }
        return ftpinfo?.username;
    };

    const setUsername = (value: string) => {
        if (editingFTPInfo) {
            setEditingFTPInfo({
                ...editingFTPInfo,
                username: value,
            });
        }
    };

    const getPassword = () => {
        if (editingFTPInfo) {
            return editingFTPInfo.password;
        }
        return ftpinfo?.password;
    };

    const setPassword = (value: string) => {
        if (editingFTPInfo) {
            setEditingFTPInfo({
                ...editingFTPInfo,
                password: value,
            });
        }
    };

    const getAppendClientMatter = (): boolean => {
        if (editingFTPInfo) {
            console.log(
                "Editing FTP Info for Attorney " +
                    editingFTPInfo.appendClientMatter
            );
            return editingFTPInfo.appendClientMatter ?? false;
        }
        console.log(
            "Editing FTP Info for Attorney " + ftpinfo?.appendClientMatter
        );
        return ftpinfo?.appendClientMatter ?? false;
    };

    const setAppendClientMatter = (value: boolean) => {
        console.log({
            "We are setting the Append Client Matter to": value,
            "Editing FTP Info": editingFTPInfo,
        });
        if (editingFTPInfo) {
            setEditingFTPInfo({
                ...editingFTPInfo,
                appendClientMatter: value,
            });
        }
    };

    const getFTPUrl = () => {
        if (editingFTPInfo) {
            return editingFTPInfo.url;
        }
        return ftpinfo?.url;
    };

    const setFTPUrl = (value: string) => {
        if (editingFTPInfo) {
            setEditingFTPInfo({
                ...editingFTPInfo,
                url: value,
            });
        }
    };

    const getFTPPort = () => {
        if (editingFTPInfo) {
            return editingFTPInfo.port;
        }
        return ftpinfo?.port;
    };

    const setFTPPort = (value: number) => {
        if (editingFTPInfo) {
            setEditingFTPInfo({
                ...editingFTPInfo,
                port: value,
            });
        }
    };

    useEffect(() => {
        if (!attorney) {
            return;
        } else {
            attorney.id !== undefined && refresh();
        }
    }, [attorney]);

    const handleOnError = (message: string) => {
        setErrorModalMessage(message);
    };

    const editFTPInfo = async (
        newFTPInfo: APIModels.FTPInfoResponse,
        oldFTPInfo: APIModels.FTPInfoResponse
    ) => {
        console.log({
            new: { ...newFTPInfo },
            old: { ...oldFTPInfo },
        });
        try {
            const response = await AttorneyService.updateFTPInfo({
                id: oldFTPInfo.id ?? APIModels.EmptyGuid,
                body: {
                    ...newFTPInfo,
                    attorneyID: attorney.id,
                },
            });
            if (response.successful && response.data) {
                setftpinfo(response.data as APIModels.FTPInfoResponse);
                setSuccessMessage("FTP Info Updated Successfully");
                setErrorModalMessage(undefined);
            } else {
                throw new Error(response.errorMessage);
            }
        } catch (error) {
            setSuccessMessage(undefined);
            handleOnError(
                "An Error occued when updating FTP Info. " +
                    (error as Error).toString() ??
                    "An unknown error occured. Please contact support."
            );
        }
        setEditing(false);
        await refresh();
    };

    const deleteFTPInfo = async (oldFTPInfo: APIModels.FTPInfoResponse) => {
        const response = await AttorneyService.deleteFTPInfo({
            id: oldFTPInfo.id ?? APIModels.EmptyGuid,
        });

        if (response.successful) {
            setftpinfo(undefined);
            setSuccessMessage("FTP Info Deleted Successfully");
            setErrorModalMessage(undefined);
        } else {
            setSuccessMessage(undefined);
            handleOnError(
                "An Error occued when deleting FTP Info. " +
                    response.errorMessage ??
                    "An unknown error occured. Please contact support."
            );
        }
        await refresh();
    };

    const createNewFTPInfo = async (newFTPInfo: APIModels.FTPInfoResponse) => {
        const response = await AttorneyService.addFTPInfo({
            attorneyId: attorney.id ?? APIModels.EmptyGuid,
            body: {
                ...newFTPInfo,
                attorneyID: attorney.id,
            },
        });

        if (response.successful && response.data) {
            setftpinfo(response.data as APIModels.FTPInfoResponse);
            setSuccessMessage("FTP Info Created Successfully");
            setErrorModalMessage(undefined);
        } else {
            setSuccessMessage(undefined);
            handleOnError(
                "An Error occued when creating FTP Info. " +
                    response.errorMessage ??
                    "An unknown error occured. Please contact support."
            );
        }
        setEditing(false);
        await refresh();
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (isCreate) {
            await createNewFTPInfo(editingFTPInfo as APIModels.FTPInfoResponse);
        } else {
            await editFTPInfo(
                editingFTPInfo as APIModels.FTPInfoResponse,
                ftpinfo as APIModels.FTPInfoResponse
            );
        }
    };

    const handleEditStart = () => {
        let mutableFTPInfo = ftpinfo;
        if (mutableFTPInfo === undefined) {
            setIsCreate(true);
            console.log("Creating new FTP Info for Attorney " + attorney.id);
            mutableFTPInfo = {
                attorneyID: attorney.id ?? APIModels.EmptyGuid,
                username: "",
                password: "",
                url: "",
                port: 0,
                appendClientMatter: false,
            } as APIModels.FTPInfoResponse;
        } else {
            setIsCreate(false);
            console.log("Editing FTP Info for Attorney " + attorney.id);
        }
        setEditingFTPInfo(mutableFTPInfo);
        setEditing(true);
    };

    return (
        <div className="card text-center">
            <Card className="text-center" component={"div"}>
                <span style={{ fontSize: "20px", placeSelf: "left" }}>
                    {" "}
                    FTP Info: {attorney.fullName}{" "}
                </span>
                <CardContent>
                    <Stack
                        direction="column"
                        spacing={4}
                        sx={{
                            justifyContent: "evenly",
                            alignItems: "center",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={4}
                            justifyContent="space-between"
                            alignItems="center"
                            flex="1"
                        >
                            <Stack
                                component="div"
                                className="form-group"
                                direction="column"
                                justifyContent="left"
                                textAlign="left"
                                width="100%"
                            >
                                <label htmlFor="username">Username</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="username"
                                    placeholder="Username"
                                    disabled={!editing}
                                    value={getUsername()}
                                    onChange={(e) =>
                                        setUsername(e.target.value)
                                    }
                                />
                            </Stack>
                            <Stack
                                component="div"
                                className="form-group"
                                direction="column"
                                justifyContent="left"
                                textAlign="left"
                                width="100%"
                            >
                                <label htmlFor="password">FTP Password</label>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    id="password"
                                    placeholder="G4nd4lfDGr8!"
                                    disabled={!editing}
                                    value={getPassword()}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                />
                            </Stack>
                            <Stack
                                component="div"
                                className="form-group"
                                justifyContent="left"
                                textAlign="left"
                                direction="column"
                                width="100%"
                            >
                                <label htmlFor="url">Url</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url"
                                    placeholder="Url"
                                    disabled={!editing}
                                    value={getFTPUrl()}
                                    onChange={(e) => setFTPUrl(e.target.value)}
                                />
                            </Stack>
                            <Stack
                                component="div"
                                className="form-group"
                                justifyContent="left"
                                textAlign="left"
                                direction="column"
                                width="100%"
                            >
                                <label htmlFor="port">Port</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="port"
                                    placeholder="Port"
                                    disabled={!editing}
                                    value={getFTPPort()}
                                    onChange={(e) =>
                                        setFTPPort(parseInt(e.target.value))
                                    }
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={4}
                            sx={{
                                justifyContent: "space-evenly",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <Stack
                                component="div"
                                className="form-group"
                                direction="row"
                                spacing={1}
                                sx={{
                                    justifyContent: "left",
                                    alignItems: "center",
                                }}
                            >
                                <Checkbox
                                    className="form-check-input"
                                    id="showPassword"
                                    aria-describedby="inputGroupPrepend"
                                    disabled={!editing}
                                    checked={showPassword}
                                    onChange={(e) =>
                                        setShowPassword(e.target.checked)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="showPassword"
                                    style={{ width: "auto" }}
                                >
                                    Show Password
                                </label>
                            </Stack>
                            <Stack
                                component="div"
                                className="form-group"
                                direction="row"
                                spacing={1}
                                sx={{
                                    justifyContent: "left",
                                    alignItems: "center",
                                }}
                            >
                                <Checkbox
                                    className="form-check-input"
                                    id="appendClientMatter"
                                    aria-describedby="inputGroupPrepend"
                                    disabled={!editing}
                                    checked={getAppendClientMatter()}
                                    onChange={(e) =>
                                        setAppendClientMatter(e.target.checked)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="appendClientMatter"
                                    style={{ width: "auto" }}
                                >
                                    Append Client Matter
                                </label>
                            </Stack>
                            {editing ? (
                                <button
                                    onClick={handleSubmit}
                                    className="btn btn-success"
                                >
                                    Save
                                </button>
                            ) : ftpinfo !== undefined ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleEditStart}
                                >
                                    Edit
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleEditStart}
                                >
                                    Create
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-danger"
                                disabled={!editing || ftpinfo === undefined}
                                onClick={async () =>
                                    await deleteFTPInfo(
                                        ftpinfo as APIModels.FTPInfoResponse
                                    )
                                }
                            >
                                Delete
                            </button>
                        </Stack>
                    </Stack>
                </CardContent>
                {(successMessage !== undefined ||
                    errorModalMessage !== undefined) && (
                    <CardFooter>
                        {errorModalMessage !== undefined &&
                            errorModalMessage !== "" && (
                                <Alert
                                    icon={<ErrorIcon fontSize="inherit" />}
                                    severity="error"
                                >
                                    {errorModalMessage}
                                </Alert>
                            )}
                        {successMessage !== undefined &&
                            successMessage !== "" && (
                                <Alert
                                    icon={
                                        <CheckCircleIcon fontSize="inherit" />
                                    }
                                    severity="success"
                                >
                                    {successMessage}
                                </Alert>
                            )}
                    </CardFooter>
                )}
            </Card>
        </div>
    );
}
